import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Container,
  useTheme,
  Paper,
  Breadcrumbs,
  Link,
  IconButton,
  Button
} from '@mui/material';
import { useUser } from "@clerk/clerk-react";
import { tokens } from '../../theme';
import { useSettings } from '../../contexts/SettingsContext';
import { getCacheItem } from '../../utils/cache';
import KeeperTeamTile from '../keepers/KeeperTeamTile';
import KeeperTeamDetailModal from '../keepers/KeeperTeamDetailModal';
import { useSleeperConnection } from '../../hooks/useSleeperConnection';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';

const LeaguePage = () => {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { user } = useUser();
  const { cachedLeagues } = useSleeperConnection();
  const { scoringType, isSuperflex } = useSettings();
  
  const [leagueDetails, setLeagueDetails] = useState(null);
  const [rosters, setRosters] = useState([]);
  const [users, setUsers] = useState({});
  const [keeperData, setKeeperData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTeamModal, setSelectedTeamModal] = useState(null);
  const [selectedScoringType, setSelectedScoringType] = useState('');

  // Determine initial scoring type
  useEffect(() => {
    if (scoringType) {
      let mappedScoringType = scoringType;
      if (isSuperflex) {
        mappedScoringType = scoringType === 'ppr' ? 'superflex_ppr' : 
                            scoringType === 'half_ppr' ? 'superflex_half_ppr' : 
                            scoringType === 'standard' ? 'superflex_standard' : 'superflex_ppr';
      }
      if (mappedScoringType !== selectedScoringType) {
        setSelectedScoringType(mappedScoringType);
      }
    }
  }, [scoringType, isSuperflex, selectedScoringType]);

  useEffect(() => {
      const fetchLeagueData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Create cache keys for this league and scoring type
      const cacheKeyBase = `league-data-${leagueId}${selectedScoringType ? `-${selectedScoringType}` : ''}`;
      const leagueCacheKey = `${cacheKeyBase}-league`;
      const usersCacheKey = `${cacheKeyBase}-users`;
      const rostersCacheKey = `${cacheKeyBase}-rosters`;
      const keeperValuesCacheKey = `${cacheKeyBase}-keeper-values`;
      const playersCacheKey = `${cacheKeyBase}-players`;
      
      // Try to get cached data first
      const cachedLeagueDetails = getCacheItem(leagueCacheKey);
      const cachedUsers = getCacheItem(usersCacheKey);
      const cachedRosters = getCacheItem(rostersCacheKey);
      const cachedKeeperValues = getCacheItem(keeperValuesCacheKey);
      const cachedPlayers = getCacheItem(playersCacheKey);
      
      // If we have all basic cached data, use it
      if (cachedLeagueDetails && cachedUsers && cachedRosters) {
        console.log('[League] Using cached league data');
        setLeagueDetails(cachedLeagueDetails);
        setUsers(cachedUsers);
        setRosters(cachedRosters);
        
        // Set optional cached data if available
        if (cachedKeeperValues) {
          setKeeperData(cachedKeeperValues);
        }
        if (cachedPlayers) {
          window.playersLookup = cachedPlayers;
        }
        
        setLoading(false);
        return;
      }
      
      console.log('[League] Cache miss, fetching fresh data');

      // First try to find the league in cached leagues from useSleeperConnection
      const cachedLeague = cachedLeagues?.find(league => league.id === leagueId);
      let externalLeagueId;
      let internalLeagueData;

      if (cachedLeague) {
        console.log('[League] Using cached league data:', cachedLeague);
        externalLeagueId = cachedLeague.external_league_id;
        internalLeagueData = cachedLeague;
      } else {
        // Fallback: fetch from API if not in cache
        const headers = {
          "Content-Type": "application/json",
        };
        
        if (user) {
          headers["X-Clerk-User-Id"] = user.id || "unknown";
          headers["X-Clerk-Email"] = user.primaryEmailAddress?.emailAddress || "";
          headers["X-Clerk-Username"] = user.username || "";
        }
        
        const internalLeagueResponse = await fetch(`/api/leagues/${leagueId}`, { headers });
        if (internalLeagueResponse.status === 404) {
          throw new Error('League not found. You may not have access to this league or it may not exist.');
        }
        if (!internalLeagueResponse.ok) {
          throw new Error('Failed to fetch league from internal API');
        }
        
        internalLeagueData = await internalLeagueResponse.json();
        externalLeagueId = internalLeagueData.external_league_id;
      }
      
      if (!externalLeagueId) {
        throw new Error('League does not have an external_league_id');
      }

      // Now fetch league details, rosters, and users from Sleeper API using external_league_id
      const [leagueResponse, rostersResponse, usersResponse] = await Promise.allSettled([
        fetch(`https://api.sleeper.app/v1/league/${externalLeagueId}`),
        fetch(`https://api.sleeper.app/v1/league/${externalLeagueId}/rosters`),
        fetch(`https://api.sleeper.app/v1/league/${externalLeagueId}/users`)
      ]);

      // Handle league details
      if (leagueResponse.status === 'fulfilled' && leagueResponse.value.ok) {
        const leagueData = await leagueResponse.value.json();
        // console.log('[League] Raw Sleeper league data:', leagueData);
        // Merge with internal league data
        const mergedLeagueDetails = {
          ...leagueData,
          ...internalLeagueData
        };
        console.log('[League] Merged league details:', mergedLeagueDetails);
        setLeagueDetails(mergedLeagueDetails);
      } else {
        throw new Error('Failed to fetch league details from Sleeper');
      }

      // Handle rosters
      if (rostersResponse.status === 'fulfilled' && rostersResponse.value.ok) {
        const rostersData = await rostersResponse.value.json();
        //console.log('[League] Raw Sleeper rosters data:', rostersData);
        // console.log('[League] Rosters count:', rostersData?.length || 0);
        setRosters(rostersData || []);
      } else {
        console.warn('Failed to fetch rosters, proceeding with empty array');
        setRosters([]);
      }

      // Handle users
      if (usersResponse.status === 'fulfilled' && usersResponse.value.ok) {
        const usersData = await usersResponse.value.json();
        // console.log('[League] Raw Sleeper users data:', usersData);
        // Convert users array to object keyed by user_id for easy lookup
        const usersMap = {};
        usersData.forEach(user => {
          usersMap[user.user_id] = user;
        });
        console.log('[League] Users map created:', usersMap);
        console.log('[League] Users count:', Object.keys(usersMap).length);
        setUsers(usersMap);
      } else {
        console.warn('Failed to fetch users, proceeding with empty object');
        setUsers({});
      }

      // Fetch keeper data if scoring type is available
      if (selectedScoringType && leagueId) {
        try {
          const headers = {
            "Content-Type": "application/json",
          };
          
          if (user) {
            headers["X-Clerk-User-Id"] = user.id || "unknown";
            headers["X-Clerk-Email"] = user.primaryEmailAddress?.emailAddress || "";
            headers["X-Clerk-Username"] = user.username || "";
          }

          // No `season` param: the backend defaults to the current NFL season.
          // This was pinned to 2025 alongside KeeperTeams.jsx, and would have
          // kept this view on last year's ADP after that one was unpinned.
          const keeperUrl = `/api/leagues/${leagueId}/keeper-values/?scoring_type=${selectedScoringType}`;
          console.log('[League] Fetching keeper values from:', keeperUrl);
          const keeperResponse = await fetch(keeperUrl, { headers });
          
          if (keeperResponse.ok) {
            const keeperResponseData = await keeperResponse.json();
            //console.table('[League] Raw keeper values data:', keeperResponseData);
            let keeperResults = [];
            if (keeperResponseData.keeper_values && Array.isArray(keeperResponseData.keeper_values)) {
              keeperResults = keeperResponseData.keeper_values;
            } else if (keeperResponseData.results && Array.isArray(keeperResponseData.results)) {
              keeperResults = keeperResponseData.results;
            } else if (Array.isArray(keeperResponseData)) {
              keeperResults = keeperResponseData;
            }
            console.table('[League] Processed keeper values data:', keeperResults);
            setKeeperData(keeperResults);
          } else {
            setKeeperData([]);
          }

          // Fetch player data for lookup
          const getApiBaseUrl = () => {
            if (process.env.NODE_ENV === "production") {
              return "https://weez-nfl-data.vercel.app";
            }
            return "";
          };

          const playersResponse = await fetch(
            `${getApiBaseUrl()}/api/players/?live_draft=true&scoring_type=${selectedScoringType.includes('ppr') ? 'ppr' : selectedScoringType.includes('half') ? 'half_ppr' : 'standard'}`,
            { headers }
          );
          
          if (playersResponse.ok) {
            const playersData = await playersResponse.json();
            let playersLookupData = {};
            (playersData.results || []).forEach(player => {
              const sleeperId = player.external_ids?.sleeper || player.external_ids?.sleeper_id || player.sleeper_id;
              if (sleeperId) {
                playersLookupData[sleeperId] = player;
              }
            });
            window.playersLookup = playersLookupData;
          }
        } catch (keeperError) {
          console.log('[League] Error fetching keeper data:', keeperError);
          setKeeperData([]);
        }
      }

    } catch (err) {
      console.error('Error fetching league data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

    if (leagueId && user?.id) {
      fetchLeagueData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId, user?.id, selectedScoringType]); // fetchLeagueData is stable within this effect

  const handleBackClick = () => {
    navigate('/');
  };

  const handleKeeperSettingsClick = () => {
    navigate(`/leagues/${leagueId}/keeper-settings`);
  };

  const handleKeepersClick = () => {
    navigate(`/leagues/${leagueId}/keepers`);
  };

  // Format season display
  const formatSeason = (season) => {
    return season ? `${season} Season` : 'Current Season';
  };

  // Get status color and text
  const getStatusInfo = (status) => {
    switch (status) {
      case 'in_season':
        return { color: colors.greenAccent[500], text: 'In Season' };
      case 'complete':
        return { color: colors.blueAccent[500], text: 'Complete' };
      case 'pre_draft':
        return { color: colors.yellowAccent[500], text: 'Pre-Draft' };
      default:
        return { color: colors.grey[500], text: 'Unknown' };
    }
  };

  // Create roster players data by combining rosters with any available keeper data
  const rosterPlayersByOwner = rosters.reduce((acc, roster, rosterIndex) => {
    const ownerId = roster.owner_id;
    if (!acc[ownerId]) {
      acc[ownerId] = [];
    }
    
    // Convert player IDs to player objects with actual player data lookup
    const rosterPlayers = (roster.players || []).map((playerId) => {
      // Look up player data from our lookup (playerId is the sleeper ID)
      const playerData = window.playersLookup?.[playerId] || { sleeper_id: playerId };
      
      // Find corresponding keeper data for this player using sleeper_id
      const playerKeeperData = keeperData.find(k => k.sleeper_id === playerId);

      // Combine player data with keeper data
      const combinedPlayer = {
        ...playerData,
        ...playerKeeperData,
        sleeper_id: playerId, // Ensure sleeper_id is consistent
        player_id: playerId, // Keep for backwards compatibility
      };
      
      return combinedPlayer;
    });
    
    acc[ownerId] = rosterPlayers;
    return acc;
  }, {});

  // Get all owners (from rosters, not just users, in case there's a mismatch)
  const allOwners = [...new Set(rosters.map(roster => roster.owner_id))].filter(id => id);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          gap: 2,
        }}
      >
        <CircularProgress size={60} sx={{ color: colors.blueAccent[500] }} />
        <Typography variant="h6" sx={{ color: colors.grey[300] }}>
          Loading league data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box sx={{ mb: 3 }}>
          <IconButton
            onClick={handleBackClick}
            sx={{ color: colors.grey[300], mb: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Box>
        <Alert 
          severity="error" 
          sx={{ 
            backgroundColor: colors.redAccent[800],
            color: colors.redAccent[100],
            '& .MuiAlert-icon': {
              color: colors.redAccent[300],
            },
            mb: 3,
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            Failed to load league data
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
        
        {/* Additional help for common issues */}
        {error.includes('League not found') && (
          <Alert 
            severity="info" 
            sx={{ 
              backgroundColor: colors.blueAccent[800],
              color: colors.blueAccent[100],
              '& .MuiAlert-icon': {
                color: colors.blueAccent[300],
              },
            }}
          >
            <Typography variant="body2">
              This could mean:
            </Typography>
            <ul style={{ margin: '8px 0', paddingLeft: '20px', color: colors.blueAccent[100] }}>
              <li>The league ID in the URL is incorrect</li>
              <li>You don't have access to this league</li>
              <li>Your Sleeper connection needs to be refreshed</li>
            </ul>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Try going back to the dashboard and refreshing your leagues, or check that you're using the correct league URL.
            </Typography>
          </Alert>
        )}
      </Container>
    );
  }

  if (!leagueDetails) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning">
          League not found or no access to this league.
        </Alert>
      </Container>
    );
  }

  const statusInfo = getStatusInfo(leagueDetails.status);

  return (
    <>
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
      {/* Navigation */}
      <Box sx={{ mb: 3 }}>
        <IconButton
          onClick={handleBackClick}
          sx={{ 
            color: colors.grey[300], 
            mb: 2,
            '&:hover': {
              backgroundColor: colors.primary[600],
            }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Breadcrumbs sx={{ color: colors.grey[400] }}>
          <Link
            color="inherit"
            onClick={handleBackClick}
            sx={{ 
              cursor: 'pointer',
              '&:hover': { color: colors.blueAccent[400] }
            }}
          >
            Dashboard
          </Link>
          <Typography color={colors.grey[100]}>
            {leagueDetails.name}
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* League Header */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          mb: 4,
          backgroundColor: colors.primary[400],
          border: `1px solid ${colors.grey[700]}`,
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <EmojiEventsIcon
            sx={{
              fontSize: 40,
              color: colors.blueAccent[500],
              mr: 2,
            }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h3"
              sx={{
                color: colors.grey[100],
                fontWeight: 700,
                mb: 1,
              }}
            >
              {leagueDetails.name}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: colors.grey[300],
                fontWeight: 400,
              }}
            >
              {formatSeason(leagueDetails.season)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={handleKeepersClick}
              variant="contained"
              startIcon={<EmojiEventsIcon />}
              sx={{
                backgroundColor: colors.blueAccent[500],
                color: colors.primary[900],
                textTransform: "uppercase",
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: colors.blueAccent[400],
                },
              }}
            >
              View Keepers
            </Button>
            <Button
              onClick={handleKeeperSettingsClick}
              variant="outlined"
              startIcon={<SettingsIcon />}
              sx={{
                borderColor: colors.blueAccent[500],
                color: colors.blueAccent[500],
                textTransform: "uppercase",
                fontWeight: 600,
                "&:hover": {
                  borderColor: colors.blueAccent[400],
                  backgroundColor: "rgba(104,112,250,0.08)",
                },
              }}
            >
              Keeper Settings
            </Button>
          </Box>
        </Box>

        {/* League Stats */}
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PeopleIcon sx={{ color: colors.grey[400], fontSize: 20 }} />
            <Typography variant="body1" sx={{ color: colors.grey[200] }}>
              {leagueDetails.total_rosters} Teams
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="body2"
              sx={{ color: colors.grey[400], fontSize: '0.875rem' }}
            >
              Status
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: statusInfo.color, fontWeight: 600 }}
            >
              {statusInfo.text}
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="body2"
              sx={{ color: colors.grey[400], fontSize: '0.875rem' }}
            >
              League ID
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: colors.grey[200], fontFamily: 'monospace' }}
            >
              {leagueDetails.league_id}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Teams Section */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            color: colors.grey[100],
            fontWeight: 600,
            mb: 1,
          }}
        >
          Teams
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: colors.grey[300],
            mb: 3,
          }}
        >
          {allOwners.length} team{allOwners.length !== 1 ? 's' : ''} in this league
        </Typography>
      </Box>

      {/* Teams Grid */}
      {allOwners.length > 0 ? (
        <Grid container spacing={3}>
          {allOwners.map((ownerId) => {
            const owner = users[ownerId];
            const rosterPlayers = rosterPlayersByOwner[ownerId] || [];
            
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={ownerId}>
                <KeeperTeamTile
                  owner={owner}
                  keeperPlayers={rosterPlayers}
                  leagueSettings={leagueDetails}
                  onClick={() => setSelectedTeamModal({ owner, keeperPlayers: rosterPlayers })}
                />
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: colors.primary[400],
            border: `1px solid ${colors.grey[700]}`,
            borderRadius: 2,
          }}
        >
          <PeopleIcon
            sx={{
              fontSize: 60,
              color: colors.grey[600],
              mb: 2,
            }}
          />
          <Typography
            variant="h5"
            sx={{
              color: colors.grey[200],
              mb: 1,
            }}
          >
            No Teams Found
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: colors.grey[400],
            }}
          >
            This league doesn't have any teams yet.
          </Typography>
        </Paper>
      )}
    </Container>

    {/* Keeper Team Detail Modal */}
    <KeeperTeamDetailModal
      open={!!selectedTeamModal}
      onClose={() => setSelectedTeamModal(null)}
      owner={selectedTeamModal?.owner}
      keeperPlayers={selectedTeamModal?.keeperPlayers || []}
      leagueSettings={leagueDetails}
    />
    </>
  );
};

export default LeaguePage;
